// Modules
import React from 'react';
import _ from 'lodash';

import { FormCard } from 'components/content-containers/cards';
import { PageContent } from 'components/page';
import Company from 'services/data/company';
import CompanyForm from 'components/models/company/company-form';
import Person from 'services/data/person';
import PhoneNumber from 'services/data/phone-number';
import SVG from 'components/widgets/icons/svg';
import appHistory from 'app-history';
import archiveItems from 'utils/archive-items';

import * as actions from '../store/actions';

export default function CompanyEdit({ id }) {
  const { data: company, refetch } = actions.useCompany(id);
  const emails = Person.getOrderedContactInfo(company.emailAddresses);
  const numbers = Person.getOrderedContactInfo(company.phoneNumbers);
  const addresses = Person.getOrderedContactInfo(company.addresses);

  const initialValues = _.pick(company, [
    'id',
    'name',
    'nickname',
    'zabbixGroup',
    'invoiceMethod',
    'paymentMethod',
    'purchaseOrderNumberRequired',
    'hasUserEscalator',
    'userEscalatorAmount',
    'disableAutofill',
    'hideBillDetails'
  ]);

  initialValues.emailAddresses = _.map(emails, email => _.pick(email, ['id', 'email', 'type', 'primary']));
  initialValues.addresses = _.map(addresses, address => _.pick(address, ['id', 'type', 'name', 'address', 'line2', 'city', 'state', 'zip', 'billing']));
  initialValues.phoneNumbers = _.map(numbers, number => _.assign(_.pick(number, ['id', 'type']), { number: PhoneNumber.format(number.number) }));
  initialValues.billingEmails = _.map(company.billingEmails, 'id');
  initialValues.defaultAgreement = _.get(company, 'defaultAgreement.id');
  initialValues.parentCompany = _.get(company, 'parentCompany.id');
  initialValues.team = _.get(company, 'team.id');
  initialValues.primaryTech = _.get(company, 'primaryTech.id');
  initialValues.externalAppIds = _.keys(company.externalAppIds);
  initialValues.sundayHours = { open: company.sundayOpen, start: company.sundayBusinessHoursStart, end: company.sundayBusinessHoursEnd };
  initialValues.mondayHours = { open: company.mondayOpen, start: company.mondayBusinessHoursStart, end: company.mondayBusinessHoursEnd };
  initialValues.tuesdayHours = { open: company.tuesdayOpen, start: company.tuesdayBusinessHoursStart, end: company.tuesdayBusinessHoursEnd };
  initialValues.wednesdayHours = { open: company.wednesdayOpen, start: company.wednesdayBusinessHoursStart, end: company.wednesdayBusinessHoursEnd };
  initialValues.thursdayHours = { open: company.thursdayOpen, start: company.thursdayBusinessHoursStart, end: company.thursdayBusinessHoursEnd };
  initialValues.fridayHours = { open: company.fridayOpen, start: company.fridayBusinessHoursStart, end: company.fridayBusinessHoursEnd };
  initialValues.saturdayHours = { open: company.saturdayOpen, start: company.saturdayBusinessHoursStart, end: company.saturdayBusinessHoursEnd };

  const onSubmit = values => {
    const update = _.clone(values);

    update.emailAddresses = archiveItems(company.emailAddresses, values.emailAddresses, { _delete: true });
    update.phoneNumbers = archiveItems(company.phoneNumbers, values.phoneNumbers, { _delete: true });
    update.addresses = archiveItems(company.addresses, values.addresses, { _delete: true });
    update.sundayOpen = values.sundayHours.open;
    update.sundayBusinessHoursStart = values.sundayHours.start;
    update.sundayBusinessHoursEnd = values.sundayHours.end;
    update.mondayOpen = values.mondayHours.open;
    update.mondayBusinessHoursStart = values.mondayHours.start;
    update.mondayBusinessHoursEnd = values.mondayHours.end;
    update.tuesdayOpen = values.tuesdayHours.open;
    update.tuesdayBusinessHoursStart = values.tuesdayHours.start;
    update.tuesdayBusinessHoursEnd = values.tuesdayHours.end;
    update.wednesdayOpen = values.wednesdayHours.open;
    update.wednesdayBusinessHoursStart = values.wednesdayHours.start;
    update.wednesdayBusinessHoursEnd = values.wednesdayHours.end;
    update.thursdayOpen = values.thursdayHours.open;
    update.thursdayBusinessHoursStart = values.thursdayHours.start;
    update.thursdayBusinessHoursEnd = values.thursdayHours.end;
    update.fridayOpen = values.fridayHours.open;
    update.fridayBusinessHoursStart = values.fridayHours.start;
    update.fridayBusinessHoursEnd = values.fridayHours.end;
    update.saturdayOpen = values.saturdayHours.open;
    update.saturdayBusinessHoursStart = values.saturdayHours.start;
    update.saturdayBusinessHoursEnd = values.saturdayHours.end;

    const currentBillingEmails = _.map(values.billingEmails, emailId => ({ id: emailId }));
    const detachBillingEmails = _(company.billingEmails).map('id').difference(values.billingEmails).map(emailId => ({ id: emailId, _detach: true })).value();
    update.billingEmails = _.concat(currentBillingEmails, detachBillingEmails);
    update.externalAppIds = _.mapValues(_.keyBy(values.externalAppIds), app => _.get(company.externalAppIds, app) || 'create');

    return Company.update(update)
      .then(() => refetch())
      .then(() => {
        appHistory.push(`/companies/${company.id}/summary`);
      });
  };

  return (
    <PageContent className="bg-medium-blue-grey">
      <div className="company-edit flex flex-equal-children">
        <FormCard className="company-form">
          <div className="form-header">
            <SVG src="svg/company.svg" className="company" />
            <h5 className="form-title">Edit Company</h5>
          </div>
          <CompanyForm
            initialValues={initialValues}
            company={company}
            onSubmit={onSubmit}
          />
        </FormCard>
      </div>
    </PageContent>
  );
}
